import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui-custom/Card";
import Button from "../ui-custom/Button";
import { Input } from "../ui-custom/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui-custom/Select";
import { History, Download, Search } from "lucide-react";

function getInitials(name) {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function getAvatarColor(name) {
  const colors = [
    "bg-sm-accent/15 text-sm-accent",
    "bg-dark-green/15 text-dark-green",
    "bg-sm-secondary/15 text-sm-secondary",
    "bg-sm-primary/15 text-sm-primary",
    "bg-destructive/15 text-destructive",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
}

export function PettyCashReports({
  expenses,
  employees,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const projects = [...new Set(expenses.map(e => e.project))];

  const filteredExpenses = expenses.filter(exp => {
    if (searchQuery && !exp.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !exp.employeeName.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedProject !== "all" && exp.project !== selectedProject) return false;
    if (selectedStatus !== "all" && exp.status !== selectedStatus) return false;
    return true;
  });

  const totalAmount = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleExport = () => {
    console.log("Exporting...");
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-sm-secondary/10 rounded">
              <History className="w-4 h-4 text-sm-secondary" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold">Expense History</CardTitle>
              <p className="text-sm text-text-gray">Complete audit trail of all expenses</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-gray" />
            <Input
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={selectedProject} onValueChange={setSelectedProject} placeholder="All Projects">
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedStatus} onValueChange={setSelectedStatus} placeholder="All Status">
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Summary Row */}
        <div className="flex items-center justify-between py-3 border-b bg-gray-bg/50 px-4 rounded-t-lg">
          <span className="text-sm text-text-gray">{filteredExpenses.length} expenses found</span>
          <span className="text-sm font-semibold text-sm-primary">Total: ₹{totalAmount.toLocaleString("en-IN")}</span>
        </div>

        {/* Expense List */}
        <div className="divide-y divide-sm-border">
          {filteredExpenses.map((expense) => (
            <div key={expense.id} className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${getAvatarColor(expense.employeeName)}`}>
                  {getInitials(expense.employeeName)}
                </div>
                <div>
                  <p className="font-medium text-primary-text">{expense.title}</p>
                  <p className="text-sm text-text-gray">
                    {expense.employeeName} • {expense.project}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-sm-primary">₹{expense.amount.toLocaleString("en-IN")}</p>
                <div className="flex items-center gap-2 justify-end mt-0.5">
                  <span className="text-xs text-text-gray">{formatDate(expense.date)}</span>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${expense.status === "Approved"
                      ? "bg-dark-green/15 text-dark-green"
                      : expense.status === "Pending"
                        ? "bg-sm-accent/15 text-sm-accent"
                        : "bg-destructive/15 text-destructive"
                    }`}>
                    {expense.status}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {filteredExpenses.length === 0 && (
            <div className="text-center py-12 text-text-gray">
              No expenses found matching your filters
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
